import React, { useEffect, useMemo, useState } from 'react';
import { backofficeDb, backofficeStorage } from '../backofficeFirebaseClient';
import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  getDoc,
  setDoc
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

const emptyPost = {
  title: '',
  slug: '',
  category: '',
  tags: [],
  excerpt: '',
  content: '',
  author: '',
  published: false,
  heroImageUrl: '',
  metaTitle: '',
  metaDescription: ''
};

const Input = (p) => (
  <input {...p} className={(p.className || '') + ' border rounded px-3 py-2 w-full'} />
);
const Textarea = (p) => (
  <textarea {...p} className={(p.className || '') + ' border rounded px-3 py-2 w-full'} />
);

const BlogContentManager = () => {
  const [loading, setLoading] = useState(false);
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeTab, setActiveTab] = useState('posts'); // 'posts' | 'categories' | 'settings'
  const [editing, setEditing] = useState(null); // post object
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [pSnap, cSnap] = await Promise.all([
        getDocs(collection(backofficeDb, 'cms_blog_posts')),
        getDocs(collection(backofficeDb, 'cms_blog_categories'))
      ]);
      setPosts(pSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setCategories(cSnap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) })));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const resetForm = () => setEditing({ ...emptyPost });

  const savePost = async () => {
    if (!editing) return;
    setLoading(true);
    try {
      const clean = {
        ...editing,
        tags: (editing.tags || []).map((t) => (t || '').toString().trim()).filter(Boolean),
        updatedAt: serverTimestamp(),
      };
      if (editing.id) {
        const r = doc(backofficeDb, 'cms_blog_posts', editing.id);
        await updateDoc(r, clean);
      } else {
        const r = await addDoc(collection(backofficeDb, 'cms_blog_posts'), {
          ...clean,
          createdAt: serverTimestamp()
        });
        clean.id = r.id;
        setEditing(clean);
      }
      await load();
    } finally { setLoading(false); }
  };

  const deletePost = async (id) => {
    if (!id) return;
    if (!window.confirm('Delete this post?')) return;
    setLoading(true);
    try {
      await deleteDoc(doc(backofficeDb, 'cms_blog_posts', id));
      setEditing(null);
      await load();
    } finally { setLoading(false); }
  };

  const uploadHero = async (file) => {
    if (!file) return;
    const path = `blog/hero/${Date.now()}_${file.name}`;
    const storageRef = ref(backofficeStorage, path);
    await uploadBytes(storageRef, file);
    const url = await getDownloadURL(storageRef);
    setEditing((e) => ({ ...(e || {}), heroImageUrl: url }));
  };

  const filteredPosts = useMemo(() => {
    const q = (search || '').toLowerCase();
    if (!q) return posts;
    return posts.filter((p) =>
      (p.title || '').toLowerCase().includes(q) ||
      (p.slug || '').toLowerCase().includes(q) ||
      (p.category || '').toLowerCase().includes(q) ||
      (p.tags || []).join(',').toLowerCase().includes(q)
    );
  }, [posts, search]);

  // Categories CRUD
  const [newCategory, setNewCategory] = useState('');
  const addCategory = async () => {
    const name = (newCategory || '').trim();
    if (!name) return;
    setLoading(true);
    try {
      await addDoc(collection(backofficeDb, 'cms_blog_categories'), { name, createdAt: serverTimestamp() });
      setNewCategory('');
      await load();
    } finally { setLoading(false); }
  };
  const removeCategory = async (id) => {
    if (!window.confirm('Delete this category?')) return;
    setLoading(true);
    try {
      await deleteDoc(doc(backofficeDb, 'cms_blog_categories', id));
      await load();
    } finally { setLoading(false); }
  };

  return (
    <div className="p-6 space-y-4">
      <div className="text-xl font-semibold">Blog Content Manager</div>
      <div className="flex gap-2 border-b pb-2">
        {['posts','categories','settings'].map((t) => (
          <button key={t} className={`px-3 py-2 rounded-t ${activeTab===t?'bg-blue-100 border border-b-white border-blue-300':'bg-white border'}`} onClick={() => setActiveTab(t)}>
            {t[0].toUpperCase()+t.slice(1)}
          </button>
        ))}
      </div>

      {activeTab === 'posts' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-1 bg-white border rounded p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="font-medium">Posts</div>
              <button className="px-2 py-1 text-sm bg-blue-600 text-white rounded" onClick={resetForm}>+ New</button>
            </div>
            <div className="mb-2">
              <Input placeholder="Search posts" value={search} onChange={(e)=>setSearch(e.target.value)} />
            </div>
            <div className="divide-y max-h-[60vh] overflow-auto">
              {filteredPosts.map((p)=> (
                <button key={p.id} className="w-full text-left py-2" onClick={()=>setEditing({...p})}>
                  <div className="font-medium text-gray-900">{p.title || '(Untitled)'}</div>
                  <div className="text-xs text-gray-500">{p.category || '—'} · {(p.published?'Published':'Draft')}</div>
                </button>
              ))}
              {filteredPosts.length===0 && <div className="text-sm text-gray-500 py-4">No posts</div>}
            </div>
          </div>

          <div className="lg:col-span-2 bg-white border rounded p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="font-medium">{editing?.id ? 'Edit Post' : 'Create Post'}</div>
              {editing?.id && (
                <button className="text-red-600" onClick={()=>deletePost(editing.id)}>Delete</button>
              )}
            </div>
            {editing ? (
              <div className="grid gap-3">
                <label className="flex flex-col">
                  <span className="text-sm font-medium mb-1">Title</span>
                  <Input value={editing.title} onChange={(e)=>setEditing({...editing, title:e.target.value})} />
                </label>
                <div className="grid sm:grid-cols-2 gap-3">
                  <label className="flex flex-col">
                    <span className="text-sm font-medium mb-1">Slug</span>
                    <Input value={editing.slug} onChange={(e)=>setEditing({...editing, slug:e.target.value.replace(/\s+/g,'-').toLowerCase()})} />
                  </label>
                  <label className="flex flex-col">
                    <span className="text-sm font-medium mb-1">Category</span>
                    <select className="border rounded px-3 py-2" value={editing.category} onChange={(e)=>setEditing({...editing, category:e.target.value})}>
                      <option value="">— Select —</option>
                      {categories.map((c)=>(<option key={c.id} value={c.name}>{c.name}</option>))}
                    </select>
                  </label>
                </div>
                <label className="flex flex-col">
                  <span className="text-sm font-medium mb-1">Tags (comma separated)</span>
                  <Input value={(editing.tags||[]).join(', ')} onChange={(e)=>setEditing({...editing, tags:e.target.value.split(',').map(s=>s.trim())})} />
                </label>
                <label className="flex flex-col">
                  <span className="text-sm font-medium mb-1">Excerpt</span>
                  <Textarea rows={3} value={editing.excerpt} onChange={(e)=>setEditing({...editing, excerpt:e.target.value})} />
                </label>
                <label className="flex flex-col">
                  <span className="text-sm font-medium mb-1">Content</span>
                  <Textarea rows={10} value={editing.content} onChange={(e)=>setEditing({...editing, content:e.target.value})} />
                </label>
                <div className="grid sm:grid-cols-2 gap-3">
                  <label className="flex flex-col">
                    <span className="text-sm font-medium mb-1">Author (Employee)</span>
                    <Input value={editing.author} onChange={(e)=>setEditing({...editing, author:e.target.value})} />
                  </label>
                  <label className="flex items-center gap-2 mt-6">
                    <input type="checkbox" checked={!!editing.published} onChange={(e)=>setEditing({...editing, published:e.target.checked})} />
                    <span className="text-sm">Published</span>
                  </label>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <label className="flex flex-col">
                    <span className="text-sm font-medium mb-1">Meta Title</span>
                    <Input value={editing.metaTitle} onChange={(e)=>setEditing({...editing, metaTitle:e.target.value})} />
                  </label>
                  <label className="flex flex-col">
                    <span className="text-sm font-medium mb-1">Meta Description</span>
                    <Input value={editing.metaDescription} onChange={(e)=>setEditing({...editing, metaDescription:e.target.value})} />
                  </label>
                </div>
                <div className="grid sm:grid-cols-2 gap-3 items-end">
                  <div>
                    <div className="text-sm font-medium mb-1">Hero Image</div>
                    <input type="file" accept="image/*" onChange={(e)=>uploadHero(e.target.files?.[0])} />
                  </div>
                  {editing.heroImageUrl && (
                    <img alt="hero" src={editing.heroImageUrl} className="h-24 object-cover rounded border" />
                  )}
                </div>
                <div className="flex justify-end gap-2 mt-2">
                  <button className="px-4 py-2 border rounded" onClick={resetForm}>Clear</button>
                  <button className="px-4 py-2 bg-green-600 text-white rounded" onClick={savePost} disabled={loading}>{loading?'Saving...':'Save Post'}</button>
                </div>
              </div>
            ) : (
              <div className="text-sm text-gray-500">Select a post from the left or click New.</div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'categories' && (
        <div className="bg-white border rounded p-4 space-y-4">
          <div className="font-medium">Categories & Tags</div>
          <div className="flex gap-2">
            <Input placeholder="New category name" value={newCategory} onChange={(e)=>setNewCategory(e.target.value)} />
            <button className="px-4 py-2 bg-blue-600 text-white rounded" onClick={addCategory} disabled={loading}>Add</button>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {categories.map((c)=> (
              <div key={c.id} className="flex items-center justify-between border rounded px-3 py-2">
                <div>{c.name}</div>
                <button className="text-red-600" onClick={()=>removeCategory(c.id)}>Remove</button>
              </div>
            ))}
            {categories.length===0 && (<div className="text-sm text-gray-500">No categories yet.</div>)}
          </div>
        </div>
      )}

      {activeTab === 'settings' && (
        <BlogSettings />
      )}
    </div>
  );
};

const BlogSettings = () => {
  const [settings, setSettings] = useState({
    siteTitle: 'ACCTOO Blog',
    siteDescription: 'Accounting tips, tutorials and product updates',
    defaultAuthor: 'ACCTOO Team',
    defaultMetaImage: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      const r = doc(backofficeDb, 'cms_blog', 'settings');
      const s = await getDoc(r);
      if (s.exists()) setSettings({ ...settings, ...(s.data() || {}) });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    load();
  }, []);

  const save = async () => {
    setLoading(true);
    try {
      const r = doc(backofficeDb, 'cms_blog', 'settings');
      await setDoc(r, { ...settings, updatedAt: serverTimestamp() }, { merge: true });
      alert('Saved');
    } finally { setLoading(false); }
  };

  return (
    <div className="bg-white border rounded p-4 space-y-3">
      <div className="font-medium">Blog Settings</div>
      <label className="flex flex-col">
        <span className="text-sm font-medium mb-1">Site Title</span>
        <Input value={settings.siteTitle} onChange={(e)=>setSettings({...settings, siteTitle:e.target.value})} />
      </label>
      <label className="flex flex-col">
        <span className="text-sm font-medium mb-1">Site Description</span>
        <Textarea rows={2} value={settings.siteDescription} onChange={(e)=>setSettings({...settings, siteDescription:e.target.value})} />
      </label>
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="flex flex-col">
          <span className="text-sm font-medium mb-1">Default Author</span>
          <Input value={settings.defaultAuthor} onChange={(e)=>setSettings({...settings, defaultAuthor:e.target.value})} />
        </label>
        <label className="flex flex-col">
          <span className="text-sm font-medium mb-1">Default Meta Image URL</span>
          <Input value={settings.defaultMetaImage} onChange={(e)=>setSettings({...settings, defaultMetaImage:e.target.value})} />
        </label>
      </div>
      <div className="flex justify-end">
        <button className="px-4 py-2 bg-green-600 text-white rounded" onClick={save} disabled={loading}>{loading?'Saving...':'Save'}</button>
      </div>
    </div>
  );
};

export default BlogContentManager;


